import React, { forwardRef } from 'react';
import classNames from 'classnames';

import { Comp, useId } from '@redsift/design-system';

import { ChartSize, ColorTheme, TooltipVariant } from '../../types';
import { PieChartLegendVariant, PieChartProps, PieChartVariant } from './types';
import { LoadingPieChart } from './LoadingPieChart';
import { EmptyPieChart } from './EmptyPieChart';
import { RenderedPieChart } from './RenderedPieChart';

const COMPONENT_NAME = 'PieChart';
const CLASSNAME = 'redsift-piechart';

/**
 * PieChart displays proportional data as slices of a circle.
 * Built on D3.js with interactive slices and customizable legends.
 *
 * Variants: `default`, `spaced`, `donut`, `spacedDonut`
 *
 * Data format: `CategoryData` `[{ key: string, value: number }]`
 *
 * @example
 * // Basic pie chart
 * const data = [
 *   { key: 'Category A', value: 30 },
 *   { key: 'Category B', value: 45 },
 *   { key: 'Category C', value: 25 },
 * ];
 * <PieChart data={data} size="medium" />
 *
 * @example
 * // Donut chart with center text
 * <PieChart
 *   data={statusData}
 *   variant="donut"
 *   text="Total"
 *   subtext="1,234"
 * />
 *
 * @example
 * // Half pie (semicircle) chart
 * <PieChart
 *   data={progressData}
 *   isHalf={true}
 *   variant="donut"
 *   text="Progress"
 *   subtext="75%"
 * />
 *
 * @example
 * // With click handler and tooltips
 * <PieChart
 *   data={breakdownData}
 *   tooltipVariant="value"
 *   onSliceClick={(d) => filterBy(d.key)}
 *   legendVariant="externalLabel"
 * />
 *
 * @example
 * // Cap small values into "Others"
 * <PieChart
 *   data={manyCategories}
 *   caping={5}
 *   others={true}
 *   sortingMethod="desc-value"
 * />
 */

export const PieChart: Comp<PieChartProps, HTMLDivElement> = forwardRef((props, ref) => {
  const {
    caping,
    chartRef,
    className,
    colorTheme = ColorTheme.default,
    data: propsData,
    emptyComponent,
    id: propsId,
    isSliceSelected = () => true,
    labelDecorator,
    legendDecorator,
    legendProps,
    legendVariant: propsLegendVariant,
    localeText = { emptyChartText: 'No Data' },
    middleText,
    onSliceClick,
    others = true,
    size = ChartSize.medium,
    sliceProps,
    sliceRole,
    sortingMethod = 'desc-value',
    subtext,
    text,
    tooltipDecorator,
    tooltipVariant = TooltipVariant.value,
    variant = PieChartVariant.spaced,
    ...forwardedProps
  } = props;
  const [_id] = useId();
  const id = propsId ?? _id;

  const legendVariant = propsLegendVariant ?? PieChartLegendVariant.none;

  if (propsData === undefined || propsData === null) {
    return <LoadingPieChart id={id} {...forwardedProps} ref={ref} />;
  }

  if (propsData.length === 0) {
    return (
      <EmptyPieChart
        data={propsData}
        emptyComponent={emptyComponent}
        id={id}
        localeText={localeText}
        size={size}
        sortingMethod={sortingMethod}
        variant={variant}
        {...forwardedProps}
        ref={ref}
      />
    );
  }

  return (
    <RenderedPieChart
      caping={caping}
      chartRef={chartRef}
      className={classNames(PieChart.className, className)}
      data={propsData}
      id={id}
      isSliceSelected={isSliceSelected}
      labelDecorator={labelDecorator}
      legendDecorator={legendDecorator}
      legendProps={legendProps}
      legendVariant={legendVariant}
      middleText={middleText}
      onSliceClick={onSliceClick}
      others={others}
      size={size}
      sliceProps={sliceProps}
      sliceRole={sliceRole}
      sortingMethod={sortingMethod}
      subtext={subtext}
      text={text}
      colorTheme={colorTheme}
      tooltipDecorator={tooltipDecorator}
      tooltipVariant={tooltipVariant}
      variant={variant}
      {...forwardedProps}
      ref={ref}
    />
  );
});
PieChart.className = CLASSNAME;
PieChart.displayName = COMPONENT_NAME;
